import { useEffect, useState } from 'react';
import type { ProductImageInfo } from '@core/types';
import GaleriaCheia from './GaleriaCheia';
import { urlDaFoto } from '../lib/consultas';
import { pedirFotoSemFundo, recorteProntoDaFoto } from '../lib/fotoRecortada';

// A foto da peça ao lado do item, SEM o fundo branco (pedido do dono,
// 01/10/2026: "os itens cadastrados viessem com uma imagem ao lado, sem fundo
// branco, onde eu posso clicar e ver"). Tocar abre a galeria inteira.
//
// Enquanto o recorte não fica pronto, um quadrado discreto ocupa o lugar —
// trocar a foto branca pela recortada piscaria na lista. Passado 1,5 s (fila
// cheia, 4G), a foto como ela é: melhor um fundo branco que um quadrado
// pulsando sem fim. Sem foto, nada: a maioria das peças ainda não tem, e uma
// coluna de vazios só faria ruído. Tocar abre a foto ORIGINAL, inteira — o
// recorte automático pode comer peça clara em fundo branco (revisão do #254).

/** A imagem da peça sem fundo; null enquanto recorta. Cai na original se não der. */
export function useFotoSemFundo(
  productId: string | null | undefined, foto: { id: string; updated_at: string; ordem?: number } | null | undefined,
): string | null {
  const pronta = recorteProntoDaFoto(productId, foto);
  const original = foto ? urlDaFoto(foto.id, foto.updated_at) : null;
  const [src, setSrc] = useState<string | null>(pronta ?? null);
  useEffect(() => {
    if (pronta) { setSrc(pronta); return; }
    if (!original) { setSrc(null); return; }
    let vivo = true;
    setSrc(null);
    const pedido = pedirFotoSemFundo(original);
    const espera = setTimeout(() => { if (vivo) setSrc((atual) => atual ?? original); }, 1500);
    pedido.promessa.then((r) => { if (vivo) setSrc(r ?? original); });
    return () => { vivo = false; clearTimeout(espera); pedido.soltar(); };
  }, [pronta, original]);
  return src;
}

export default function MiniaturaDaPeca({ productId, fotos, descricao, tamanho = 48 }: {
  productId: string | null | undefined;
  /** A galeria da peça, em ordem (a primeira é a principal). */
  fotos: ProductImageInfo[];
  /** Para o leitor de tela: "Ver a foto de …". */
  descricao: string;
  tamanho?: number;
}) {
  const [aberta, setAberta] = useState(false);
  const principal = fotos[0] ?? null;
  const src = useFotoSemFundo(productId, principal);
  if (!principal) return null;
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setAberta(true); }}
              className="shrink-0" aria-label={`Ver a foto de ${descricao}`}
              style={{ width: tamanho, height: tamanho }}>
        {src ? (
          <img src={src} alt="" loading="lazy" className="h-full w-full object-contain" />
        ) : (
          <span className="block h-full w-full animate-pulse bg-edge/40" aria-hidden="true" />
        )}
      </button>
      {aberta && (
        <GaleriaCheia fotos={fotos} urlDe={(f) => urlDaFoto(f.id, f.updated_at)} aoFechar={() => setAberta(false)} />
      )}
    </>
  );
}
